// src/tabs/FiscalTab.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { 
  FileText, Search, Filter, ShieldAlert, Eye, Download, Ban, RefreshCw, Trash2,
  ChevronLeft, ChevronRight, HelpCircle, Plus, Calendar, BarChart3, 
  TrendingUp, Landmark, Percent, DownloadCloud, Layers, Mail, Check 
} from "lucide-react";
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid 
} from "recharts";
import { fiscalService } from "../services/fiscalService";
import { pb } from "../services/pocketbase";
import CancelInvoiceModal from "../components/fiscal/CancelInvoiceModal";
import CloneForDevolucaoModal from "../components/fiscal/CloneForDevolucaoModal";
import CloneForGarantiaModal from "../components/fiscal/CloneForGarantiaModal";
import InvoiceGenerationModal from "../components/modals/InvoiceGenerationModal";
import VoidNumberModal from "../components/fiscal/VoidNumberModal";

export default function FiscalTab({ findCatalogItem }) {
  // Configurações Fiscais e Sub-Abas
  const [configs, setConfigs] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState("history"); // "history" ou "dashboard"
  
  // Filtros Unificados
  const [selectedCnpj, setSelectedCnpj] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [datePreset, setDatePreset] = useState("30"); // "7", "15", "30", "custom"
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Paginação e Listagem (Histórico)
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatusTab, setActiveStatusTab] = useState("todas");

  // Dados do Dashboard (Completo sem paginação)
  const [dashInvoices, setDashInvoices] = useState([]);
  const [loadingDash, setLoadingDash] = useState(false);
  const [taxRate, setTaxRate] = useState(4.5); // Alíquota do Simples Nacional padrão (%)

  // States dos Modais
  const [cancelingInvoice, setCancelingInvoice] = useState(null);
  const [devolucaoInvoice, setDevolucaoInvoice] = useState(null);
  const [garantiaInvoice, setGarantiaInvoice] = useState(null);
  const [exitGarantiaInvoice, setExitGarantiaInvoice] = useState(null);
  const [resendingInvoice, setResendingInvoice] = useState(null);
  const [isAvulsoModalOpen, setIsAvulsoModalOpen] = useState(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [syncingIds, setSyncingIds] = useState({});

  // --- CARREGAR CONFIGURAÇÕES ---
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const data = await fiscalService.fetchConfigs();
        setConfigs(data);
      } catch (err) {
        console.error("Erro ao carregar CNPJs configurados:", err);
      }
    };
    loadConfigs();
  }, []);

  // --- MANIPULAÇÃO DE PRESETS DE DATA ---
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;

    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - parseInt(preset));

    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
    setPage(1);
  };

  // --- CARREGAR HISTÓRICO DE NOTAS (PAGINADO) ---
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fiscalService.fetchInvoices(
        page, 
        perPage, 
        selectedCnpj, 
        searchTerm, 
        startDate, 
        endDate,
        activeStatusTab
      );
      setInvoices(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.totalItems || 0);
    } catch (err) {
      console.error("Erro ao buscar notas fiscais paginadas:", err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, selectedCnpj, searchTerm, startDate, endDate, activeStatusTab]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // --- CARREGAR HISTÓRICO COMPLETO PARA O DASHBOARD ---
  const loadDashInvoices = useCallback(async () => {
    setLoadingDash(true);
    try {
      const data = await fiscalService.fetchAllInvoices(
        selectedCnpj, 
        searchTerm, 
        startDate, 
        endDate
      );
      setDashInvoices(data || []);
    } catch (err) {
      console.error("Erro ao buscar faturamento completo para o dashboard:", err);
    } finally {
      setLoadingDash(false);
    }
  }, [selectedCnpj, searchTerm, startDate, endDate]);

  useEffect(() => {
    loadDashInvoices();
  }, [loadDashInvoices]);

  // --- ESCUTAR ATUALIZAÇÕES EM TEMPO REAL ---
  useEffect(() => {
    // Atualiza o estado em tempo real caso chegue algum callback de webhook do n8n
    pb.collection("fiscal_invoices").subscribe("*", (e) => {
      if (e.action === "update" || e.action === "create") {
        setInvoices((prev) => {
          const exists = prev.some((item) => item.id === e.record.id);
          if (exists) {
            return prev.map((item) => (item.id === e.record.id ? { ...item, ...e.record, expand: { ...item.expand, ...e.record.expand } } : item));
          } else {
            return [e.record, ...prev];
          }
        });
        setDashInvoices((prev) => {
          const exists = prev.some((item) => item.id === e.record.id);
          return exists
            ? prev.map((item) => (item.id === e.record.id ? { ...item, ...e.record, expand: { ...item.expand, ...e.record.expand } } : item))
            : [e.record, ...prev];
        });
      } else if (e.action === "delete") {
        setInvoices((prev) => prev.filter((item) => item.id !== e.record.id));
        setDashInvoices((prev) => prev.filter((item) => item.id !== e.record.id));
      }
    });

    return () => {
      pb.collection("fiscal_invoices").unsubscribe("*").catch(() => {});
    };
  }, []);

  // --- OTIMIZAÇÃO: SINCRONIZAR INDIVIDUALMENTE SEM RECARREGAR A TELA INTEIRA ---
  const syncSingleInvoice = async (invoice, sendEmail = false) => {
    setSyncingIds((prev) => ({ ...prev, [invoice.id]: true }));
    try {
      const cnpjEmissor = (
        invoice.cnpj_emissor || 
        invoice.cnpj || 
        invoice.full_data?.cnpj_emissor || 
        invoice.full_data?.cnpj || 
        invoice.focus_payload?.cnpj_emissor || 
        invoice.full_data?.focus_payload?.cnpj_emissor || 
        ""
      ).replace(/\D/g, "");
      const config = configs.find(
        (c) => c.cnpj.replace(/\D/g, "") === cnpjEmissor
      ) || configs[0];

      if (!config) {
        throw new Error("Configuração fiscal não localizada para o CNPJ desta nota.");
      }

      const nfeAmbiente = invoice.ambiente || invoice.full_data?.ambiente || config.ambiente || "homologacao";
      const token = (nfeAmbiente === "producao" ? config.token_producao : config.token_homologacao || "").trim();

      const focusRef = invoice.focus_ref || invoice.id;

      const payload = {
        id: invoice.id,
        ref: focusRef,
        ambiente: nfeAmbiente,
        token: token,
        pb_token: pb.authStore.token,
        send_email: sendEmail,
      };

      const result = await fiscalService.syncInvoice(payload);

      // Atualiza o estado local imediatamente. O subscribe também receberá o update.
      if (result && result.id) {
        setInvoices((prev) =>
          prev.map((item) => (item.id === invoice.id ? { ...item, ...result, expand: { ...item.expand, ...result.expand } } : item))
        );
        setDashInvoices((prev) =>
          prev.map((item) => (item.id === invoice.id ? { ...item, ...result, expand: { ...item.expand, ...result.expand } } : item))
        );
      }
      return { success: true };
    } catch (err) {
      console.error(`Erro ao sincronizar nota ${invoice.id}:`, err);
      
      // Se a nota estava travada em "processando", marca como erro no PB para liberar o botão de reenvio
      const currentStatus = invoice.nfe_status || invoice.full_data?.nfe_status;
      if (currentStatus === "processando") {
        try {
          const updatedRecord = await pb.collection("fiscal_invoices").update(invoice.id, {
            nfe_status: "erro",
            nfe_error_message: `Erro na consulta da Focus NFe: ${err.message || "A nota pode não ter sido emitida com sucesso."}`
          });
          setInvoices((prev) =>
            prev.map((item) => (item.id === invoice.id ? updatedRecord : item))
          );
        } catch (updateErr) {
          console.error("Erro ao atualizar status para erro após sync falhar:", updateErr);
        }
      }
      return { success: false, error: err.message || "Erro desconhecido" };
    } finally {
      setSyncingIds((prev) => {
        const copy = { ...prev };
        delete copy[invoice.id];
        return copy;
      });
    }
  };

  const handleDeleteInvoiceRecord = async (invoice) => {
    const confirm = window.confirm(`Deseja realmente APAGAR o rascunho desta nota fiscal de R$ ${(invoice.valor_total || invoice.focus_payload?.valor_total || "0.00")} do pedido ${invoice.order_number || "AVULSO"}? (Isso apenas limpa o registro local do sistema, ideal para apagar notas com erro de envio)`);
    if (!confirm) return;

    try {
      await pb.collection("fiscal_invoices").delete(invoice.id);
      alert("Registro de nota fiscal apagado com sucesso!");
      setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
      setDashInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
    } catch (err) {
      console.error("Erro ao apagar nota fiscal:", err);
      alert(`Erro ao apagar do banco: ${err.message}`);
    }
  };

  const handleSync = async (invoice) => {
    const res = await syncSingleInvoice(invoice);
    if (res.success) {
      alert("Status da nota sincronizado com sucesso!");
    } else {
      alert(`Erro ao sincronizar status: ${res.error}`);
    }
  };

  const handleSendEmail = async (invoice) => {
    setSyncingIds((prev) => ({ ...prev, [invoice.id]: true }));
    try {
      const payload = {
        ref: invoice.id,
        pb_token: pb.authStore.token,
      };

      const result = await fiscalService.sendInvoiceEmail(payload);
      if (result && result.success !== false) {
        alert("E-mail de notificação enviado para o cliente!");
      } else {
        throw new Error(result?.error || "Erro no processamento do e-mail.");
      }
    } catch (err) {
      console.error(`Erro ao enviar e-mail da nota ${invoice.id}:`, err);
      alert(`Erro ao enviar e-mail: ${err.message}`);
    } finally {
      setSyncingIds((prev) => {
        const copy = { ...prev };
        delete copy[invoice.id];
        return copy;
      });
    }
  };

  const handleBulkSync = async () => {
    const toSync = invoices.filter(
      (invoice) => {
        const status = invoice.full_data?.nfe_status || "pendente";
        return status !== "autorizada" && status !== "cancelada";
      }
    );

    if (toSync.length === 0) {
      alert("Nenhuma nota fiscal pendente de sincronização encontrada na página atual.");
      return;
    }

    if (!window.confirm(`Deseja sincronizar o status de ${toSync.length} nota(s) fiscal(is) sequencialmente?`)) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const invoice of toSync) {
      const res = await syncSingleInvoice(invoice);
      if (res.success) {
        successCount++;
      } else {
        failCount++;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    setLoading(false);
    alert(`Sincronização em lote concluída!\nSucessos: ${successCount}\nFalhas: ${failCount}`);
  };

  // --- EXPORTAÇÃO EM MASSA (ZIP - XML E PDF) ---
  const handleBulkDownload = async () => {
    try {
      setLoading(true);
      
      // Filtra apenas notas autorizadas carregadas no período selecionado
      const authorizedInvoices = dashInvoices.filter((invoice) => {
        const status = invoice.nfe_status || invoice.full_data?.nfe_status;
        return status === "autorizada";
      });

      if (authorizedInvoices.length === 0) {
        alert("Nenhuma nota fiscal autorizada encontrada no período selecionado.");
        return;
      }

      // Importa dinamicamente a biblioteca JSZip para carregar apenas sob demanda
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      
      let xmlFolder = zip.folder("XMLs");
      let pdfFolder = zip.folder("PDFs");
      
      let xmlCount = 0;
      let pdfCount = 0;
      let skippedCount = 0;

      for (const invoice of authorizedInvoices) {
        const numNota = invoice.nfe_numero || invoice.full_data?.nfe_numero || invoice.order_number || invoice.id;
        let xmlContent = invoice.full_data?.nfe_xml_content || "";
        let pdfBytes = null;

        // 1. Tenta extrair o PDF de Base64 se armazenado em full_data
        if (invoice.full_data?.nfe_pdf_base64) {
          try {
            const binaryString = atob(invoice.full_data.nfe_pdf_base64);
            const len = binaryString.length;
            pdfBytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              pdfBytes[i] = binaryString.charCodeAt(i);
            }
          } catch (e) {
            console.error(`Erro ao converter base64 do PDF da nota ${numNota}:`, e);
          }
        }

        // 2. Se existirem campos de arquivo nativos no PocketBase (S3/Files storage)
        const xmlFile = Array.isArray(invoice.nfe_xml) ? invoice.nfe_xml[0] : invoice.nfe_xml;
        if (xmlFile && !xmlContent) {
          try {
            const url = pb.files.getUrl(invoice, xmlFile, { token: pb.authStore.token });
            const res = await fetch(url);
            if (res.ok) {
              xmlContent = await res.text();
            }
          } catch (e) {
            console.error(`Erro ao baixar nfe_xml da nota ${numNota}:`, e);
          }
        }

        const pdfFile = Array.isArray(invoice.nfe_pdf) ? invoice.nfe_pdf[0] : invoice.nfe_pdf;
        if (pdfFile && !pdfBytes) {
          try {
            const url = pb.files.getUrl(invoice, pdfFile, { token: pb.authStore.token });
            const res = await fetch(url);
            if (res.ok) {
              const arrayBuffer = await res.arrayBuffer();
              pdfBytes = new Uint8Array(arrayBuffer);
            }
          } catch (e) {
            console.error(`Erro ao baixar nfe_pdf da nota ${numNota}:`, e);
          }
        }

        // Adiciona ao ZIP
        if (xmlContent) {
          xmlFolder.file(`NFe_${numNota}.xml`, xmlContent);
          xmlCount++;
        }
        if (pdfBytes) {
          pdfFolder.file(`NFe_${numNota}.pdf`, pdfBytes);
          pdfCount++;
        }

        if (!xmlContent && !pdfBytes) {
          skippedCount++;
        }
      }

      if (xmlCount === 0 && pdfCount === 0) {
        alert(
          "As notas encontradas no período ainda não possuem arquivos armazenados no banco de dados. " +
          "Utilize o botão 'Sincronizar' (ou atualize os webhooks do n8n) para salvar o XML/PDF no banco de dados."
        );
        return;
      }

      // Gera o arquivo ZIP
      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Exportacao_Fiscal_${startDate}_a_${endDate}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      let msg = `Exportação concluída com sucesso!\n\n- ${xmlCount} XML(s) salvos\n- ${pdfCount} PDF(s) salvos`;
      if (skippedCount > 0) {
        msg += `\n\n* Observação: ${skippedCount} notas não continham arquivos salvos localmente e foram puladas. Sincronize-as para salvá-las no banco.`;
      }
      alert(msg);
    } catch (err) {
      console.error("Erro ao gerar arquivos de exportação fiscal:", err);
      alert("Erro ao exportar arquivos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- ESTATÍSTICAS E GRÁFICOS DO DASHBOARD (DASH FISCAL) ---
  const dashStats = useMemo(() => {
    const stats = {};
    
    // Inicializa com as empresas cadastradas nas configs fiscais
    configs.forEach((c) => {
      const cleanCnpj = c.cnpj.replace(/\D/g, "");
      stats[cleanCnpj] = {
        razao_social: c.razao_social,
        cnpj: c.cnpj,
        total: 0,
        count: 0,
        canceladas: 0,
        erros: 0
      };
    });

    // Grupo de fallback
    stats["outros"] = {
      razao_social: "Outros Emissores",
      cnpj: "",
      total: 0,
      count: 0,
      canceladas: 0,
      erros: 0
    };

    let totalGeral = 0;
    let countGeral = 0;

    dashInvoices.forEach((inv) => {
      const status = inv.nfe_status || inv.full_data?.nfe_status || "pendente";
      const cleanCnpj = (inv.cnpj_emissor || inv.full_data?.cnpj_emissor || inv.cnpj || "").replace(/\D/g, "");
      const val = parseFloat(
        inv.valor_total ||
        inv.total_value ||
        inv.focus_payload?.valor_total ||
        inv.full_data?.valor_total ||
        inv.full_data?.focus_payload?.valor_total ||
        inv.full_data?.order?.payment?.total ||
        0
      );

      const targetGroup = stats[cleanCnpj] ? cleanCnpj : "outros";

      if (status === "autorizada") {
        stats[targetGroup].total += val;
        stats[targetGroup].count += 1;
        totalGeral += val;
        countGeral += 1;
      } else if (status === "cancelada") {
        stats[targetGroup].canceladas += 1;
      } else if (status === "erro" || status === "erro_autorizacao") {
        stats[targetGroup].erros += 1;
      }
    });

    // Remove "outros" se não contiver notas
    if (stats["outros"].total === 0 && stats["outros"].count === 0 && stats["outros"].canceladas === 0 && stats["outros"].erros === 0) {
      delete stats["outros"];
    }

    return {
      companies: Object.values(stats),
      totalGeral,
      countGeral
    };
  }, [dashInvoices, configs]);

  // Prepara dados diários para o gráfico Recharts
  const chartData = useMemo(() => {
    if (dashInvoices.length === 0) return [];

    const dailyData = {};

    // Cria as datas do range para evitar furos na timeline
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split("T")[0];
      const displayDate = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      
      dailyData[dateKey] = {
        dateStr: dateKey,
        displayDate,
        semprejoias: 0,
        sublismith: 0,
        outros: 0,
        total: 0
      };
    }

    dashInvoices.forEach((inv) => {
      const status = inv.nfe_status || inv.full_data?.nfe_status || "pendente";
      if (status !== "autorizada") return;
      const rawDate = inv.focus_payload?.data_emissao || inv.full_data?.focus_payload?.data_emissao || inv.created;
      if (!rawDate) return;
      
      const dateKey = rawDate.split("T")[0];
      if (dailyData[dateKey]) {
        const val = parseFloat(
          inv.valor_total ||
          inv.total_value ||
          inv.focus_payload?.valor_total ||
          inv.full_data?.valor_total ||
          inv.full_data?.focus_payload?.valor_total ||
          inv.full_data?.order?.payment?.total ||
          0
        );
        const cleanCnpj = (inv.cnpj_emissor || inv.full_data?.cnpj_emissor || inv.cnpj || "").replace(/\D/g, "");
        
        let keyName = "outros";
        const config = configs.find(c => c.cnpj.replace(/\D/g, "") === cleanCnpj);
        if (config) {
          keyName = config.razao_social.toLowerCase().includes("subli") ? "sublismith" : "semprejoias";
        }
        
        dailyData[dateKey][keyName] = parseFloat((dailyData[dateKey][keyName] + val).toFixed(2));
        dailyData[dateKey].total = parseFloat((dailyData[dateKey].total + val).toFixed(2));
      }
    });

    return Object.values(dailyData).sort((a, b) => a.dateStr.localeCompare(b.dateStr));
  }, [dashInvoices, configs, startDate, endDate]);

  // --- HELPERS GERAIS ---
  const getFullUrl = (path, ambiente) => {
    if (!path) return "";
    let cleanPath = path;
    if (cleanPath.startsWith("http://arquivos_development") || cleanPath.startsWith("https://arquivos_development")) {
      return cleanPath.replace(/^https?:\/\/arquivos_development/, "https://homologacao.focusnfe.com.br/arquivos_development");
    }
    if (cleanPath.startsWith("http://arquivos") || cleanPath.startsWith("https://arquivos")) {
      return cleanPath.replace(/^https?:\/\/arquivos/, "https://api.focusnfe.com.br/arquivos");
    }
    if (cleanPath.startsWith("http")) return cleanPath;
    const base = ambiente === "producao" ? "https://api.focusnfe.com.br" : "https://homologacao.focusnfe.com.br";
    return base + cleanPath;
  };

  const checkCanCancel = (invoice) => {
    const status = invoice.nfe_status || invoice.full_data?.nfe_status;
    if (status !== "autorizada") return false;
    
    const emissionDateStr = 
      invoice.nfe_data_emissao || 
      invoice.focus_payload?.data_emissao || 
      invoice.full_data?.nfe_data_emissao || 
      invoice.full_data?.focus_payload?.data_emissao || 
      invoice.updated || 
      invoice.created;
    if (!emissionDateStr) return false;
    const emissionDate = new Date(emissionDateStr);
    const diffMs = new Date() - emissionDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours < 24;
  };

  const getStatusBadge = (status, errorMsg) => {
    switch (status) {
      case "processando":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            Processando
          </span>
        );
      case "autorizada":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Autorizada
          </span>
        );
      case "cancelada":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-650 border border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
            Cancelada
          </span>
        );
      case "erro":
        return (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-100 cursor-help"
            title={errorMsg || "Rejeição SEFAZ"}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
            Erro SEFAZ
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
            Pendente
          </span>
        );
    }
  };

  const getInvoiceConfig = (invoice) => {
    if (!invoice) return configs[0];
    const cleanCnpj = (
      invoice.cnpj_emissor || 
      invoice.cnpj || 
      invoice.full_data?.cnpj_emissor || 
      invoice.full_data?.cnpj || 
      invoice.focus_payload?.cnpj_emissor || 
      invoice.full_data?.focus_payload?.cnpj_emissor || 
      ""
    ).replace(/\D/g, "");
    return configs.find(c => c.cnpj.replace(/\D/g, "") === cleanCnpj) || configs[0];
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50 animate-fade-in">
      {/* Modais de Controle */}
      {cancelingInvoice && (
        <CancelInvoiceModal
          isOpen={!!cancelingInvoice}
          onClose={() => setCancelingInvoice(null)}
          invoice={cancelingInvoice}
          config={getInvoiceConfig(cancelingInvoice)}
          onSuccess={loadInvoices}
        />
      )}
      {devolucaoInvoice && (
        <CloneForDevolucaoModal
          isOpen={!!devolucaoInvoice}
          onClose={() => setDevolucaoInvoice(null)}
          invoice={devolucaoInvoice}
          configs={configs}
          findCatalogItem={findCatalogItem}
          onSuccess={loadInvoices}
        />
      )}
      {garantiaInvoice && (
        <CloneForGarantiaModal
          isOpen={!!garantiaInvoice}
          onClose={() => setGarantiaInvoice(null)}
          invoice={garantiaInvoice}
          configs={configs}
          findCatalogItem={findCatalogItem}
          onSuccess={loadInvoices}
          isExitMode={false}
        />
      )}
      {exitGarantiaInvoice && (
        <CloneForGarantiaModal
          isOpen={!!exitGarantiaInvoice}
          onClose={() => setExitGarantiaInvoice(null)}
          invoice={exitGarantiaInvoice}
          configs={configs}
          findCatalogItem={findCatalogItem}
          onSuccess={loadInvoices}
          isExitMode={true}
        />
      )}
      {resendingInvoice && (
        <InvoiceGenerationModal
          isOpen={!!resendingInvoice}
          invoice={resendingInvoice}
          findCatalogItem={findCatalogItem}
          onClose={() => setResendingInvoice(null)}
          onSuccess={loadInvoices}
        />
      )}
      {isAvulsoModalOpen && (
        <InvoiceGenerationModal
          isOpen={isAvulsoModalOpen}
          isAvulso={true}
          findCatalogItem={findCatalogItem}
          onClose={() => setIsAvulsoModalOpen(false)}
          onSuccess={loadInvoices}
        />
      )}
      {isVoidModalOpen && (
        <VoidNumberModal
          isOpen={isVoidModalOpen}
          onClose={() => setIsVoidModalOpen(false)}
          configs={configs}
        />
      )}

      {/* 1. Header Fixo */}
      <div className="bg-white border-b border-slate-200 p-6 shrink-0 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
            <FileText className="text-blue-600 animate-pulse" size={24} />
            Área Fiscal Integrada (NF-e)
          </h1>
          <p className="text-slate-400 text-xs mt-0.5">
            Gerenciamento fiscal, estimativas de impostos Simples Nacional e exportações contábeis.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Navegação de Sub-abas */}
          <div className="bg-slate-100 p-1 rounded-xl flex gap-1 border border-slate-200">
            <button
              onClick={() => setActiveSubTab("history")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubTab === "history"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-550 hover:text-slate-800"
              }`}
            >
              <Layers size={14} />
              Histórico
            </button>
            <button
              onClick={() => setActiveSubTab("dashboard")}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeSubTab === "dashboard"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-550 hover:text-slate-800"
              }`}
            >
              <BarChart3 size={14} />
              Painel Analítico
            </button>
          </div>

          {/* Emissão Avulsa */}
          <button
            onClick={() => setIsAvulsoModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-black px-4 py-2 rounded-xl shadow-lg shadow-blue-100 flex items-center gap-1.5 transition-all cursor-pointer hover:scale-[1.02] active:scale-95 animate-pulse-once"
          >
            <Plus size={16} />
            Emissão Avulsa
          </button>

          {/* Inutilizar Numeração */}
          <button
            onClick={() => setIsVoidModalOpen(true)}
            className="bg-red-650 hover:bg-red-750 text-white text-xs font-black px-4 py-2 rounded-xl shadow-lg shadow-red-100 flex items-center gap-1.5 transition-all cursor-pointer hover:scale-[1.02] active:scale-95"
            style={{ backgroundColor: "#dc2626" }}
            title="Inutilizar faixa de numeração na SEFAZ"
          >
            <ShieldAlert size={16} />
            Inutilizar Faixa
          </button>
        </div>
      </div>

      {/* 2. Filtro Unificado Bar (Visível em ambas as abas) */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center flex-1 min-w-0">
          {/* Emitente selection */}
          <div className="relative w-full sm:w-56">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <select
              className="pl-8 pr-4 py-1.5 w-full border border-slate-200 rounded-xl text-xs font-bold text-slate-650 bg-white outline-none cursor-pointer appearance-none"
              value={selectedCnpj}
              onChange={(e) => {
                setSelectedCnpj(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos os Emitentes</option>
              {configs.map((c) => (
                <option key={c.id} value={c.cnpj}>
                  {c.razao_social}
                </option>
              ))}
            </select>
          </div>

          {/* Date presets */}
          <div className="bg-slate-100 p-0.5 rounded-xl flex gap-0.5 border border-slate-200">
            {["7", "15", "30", "custom"].map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all cursor-pointer ${
                  datePreset === preset
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {preset === "custom" ? "Personalizado" : `${preset} Dias`}
              </button>
            ))}
          </div>

          {/* Date range pickers (visible if custom is selected) */}
          {datePreset === "custom" && (
            <div className="flex items-center gap-1.5 animate-fade-in">
              <input
                type="date"
                className="border border-slate-250 rounded-xl px-2.5 py-1.5 text-xs text-slate-650 font-bold bg-white outline-none"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              />
              <span className="text-slate-400 text-xs font-bold">até</span>
              <input
                type="date"
                className="border border-slate-250 rounded-xl px-2.5 py-1.5 text-xs text-slate-650 font-bold bg-white outline-none"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              />
            </div>
          )}

          {/* Search bar */}
          {activeSubTab === "history" && (
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Buscar nota, pedido ou cliente..."
                className="pl-8 pr-4 py-1.5 w-full border border-slate-250 rounded-xl text-xs outline-none focus:border-blue-500 font-medium"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>

        {/* Bulk Download Action */}
        <div className="flex items-center gap-3 shrink-0">
          {activeSubTab === "history" && (
            <button
              onClick={handleBulkSync}
              disabled={loading || invoices.length === 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-black px-5 py-2.5 rounded-full shadow-lg shadow-blue-100 flex items-center gap-2 transition-all cursor-pointer hover:scale-[1.02] active:scale-95 animate-fade-in"
              title="Sincronizar status de todas as notas não finalizadas da página atual"
            >
              <RefreshCw size={15} />
              Sincronizar Status da Página
            </button>
          )}

          <button
            onClick={handleBulkDownload}
            disabled={loadingDash || dashInvoices.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-black px-5 py-2.5 rounded-full shadow-lg shadow-emerald-100 flex items-center gap-2 transition-all cursor-pointer hover:scale-[1.02] active:scale-95 animate-fade-in"
            title="Download em massa de todos os XMLs e PDFs no período"
          >
            <DownloadCloud size={15} />
            Download em Massa (ZIP)
          </button>
          
          <button
            onClick={() => { loadInvoices(); loadDashInvoices(); }}
            className="p-2 border border-slate-250 rounded-xl hover:bg-slate-50 transition-colors text-slate-500 cursor-pointer"
            title="Atualizar lista"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* 3. Sub-Abas Container */}
      <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0 bg-slate-50">
        
        {/* --- ABA DE HISTÓRICO DE NOTAS --- */}
        {activeSubTab === "history" && (
          <div className="p-6 h-full flex flex-col">
            {/* Status Tabs Bar */}
            <div className="flex border-b border-slate-200 mb-4 gap-2 overflow-x-auto select-none pb-2 shrink-0">
              {[
                { id: "todas", label: "Todas" },
                { id: "pendente", label: "Pendentes" },
                { id: "processando", label: "Processando" },
                { id: "autorizada", label: "Autorizadas" },
                { id: "cancelada", label: "Canceladas" },
                { id: "erro", label: "Erros" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveStatusTab(tab.id);
                    setPage(1);
                  }}
                  className={`px-4 py-2 border-b-2 font-bold text-xs transition-all cursor-pointer whitespace-nowrap ${
                    activeStatusTab === tab.id
                      ? "border-blue-600 text-blue-650"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-450 gap-3 py-20">
                <RefreshCw className="animate-spin text-blue-500" size={32} />
                <span className="text-sm font-bold">Carregando histórico fiscal...</span>
              </div>
            ) : invoices.length === 0 ? (
              <div className="flex-1 text-center py-20 bg-white border border-slate-200 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center">
                <ShieldAlert size={48} className="text-slate-350 mb-3" />
                <p className="text-slate-500 font-bold text-sm">Nenhuma NF-e localizada</p>
                <p className="text-slate-400 text-xs mt-1">
                  Não encontramos notas fiscais emitidas no período ou filtros selecionados.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
                <div className="overflow-x-auto flex-1">
                  <table className="w-full text-left text-xs border-collapse font-medium text-slate-700">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider select-none">
                        <th className="p-4 w-32">Nº Pedido</th>
                        <th className="p-4">Cliente</th>
                        <th className="p-4 w-44">Empresa</th>
                        <th className="p-4 text-center w-28">Status</th>
                        <th className="p-4 w-32 text-center">Nº Nota / Série</th>
                        <th className="p-4 text-right w-28">Valor</th>
                        <th className="p-4 w-32 text-center">Data Emissão</th>
                        <th className="p-4 text-center w-[22rem]">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => {
                        const rawStatus = invoice.nfe_status || invoice.full_data?.nfe_status || "pendente";
                        const status = rawStatus === "erro_autorizacao" ? "erro" : rawStatus;
                        const numNota = invoice.nfe_numero || invoice.full_data?.nfe_numero || "-";
                        const serie = invoice.nfe_serie || invoice.full_data?.nfe_serie || "";
                        const ambiente = invoice.ambiente || invoice.full_data?.nfe_ambiente || "homologacao";
                        const errMessage = invoice.nfe_error_message || invoice.full_data?.nfe_error_message || "";
                        const cleanCnpj = (invoice.cnpj_emissor || invoice.full_data?.cnpj_emissor || invoice.cnpj || "").replace(/\D/g, "");
                        const emitterConfig = configs.find(c => c.cnpj.replace(/\D/g, "") === cleanCnpj);
                        
                        const danfeUrl = invoice.nfe_pdf ? pb.files.getUrl(invoice, invoice.nfe_pdf, { token: pb.authStore.token }) : (invoice.nfe_pdf_url ? getFullUrl(invoice.nfe_pdf_url, ambiente) : (invoice.full_data?.nfe_pdf_url ? getFullUrl(invoice.full_data.nfe_pdf_url, ambiente) : ""));
                        const xmlUrl = invoice.nfe_xml ? pb.files.getUrl(invoice, invoice.nfe_xml, { token: pb.authStore.token }) : (invoice.nfe_xml_url ? getFullUrl(invoice.nfe_xml_url, ambiente) : (invoice.full_data?.nfe_xml_url ? getFullUrl(invoice.full_data.nfe_xml_url, ambiente) : ""));
                        const emissionDate = invoice.focus_payload?.data_emissao || invoice.full_data?.focus_payload?.data_emissao || invoice.created;

                        const showCancel = checkCanCancel(invoice);
                        const isDevolucaoNFe = invoice.tipo_nota === "devolucao" || invoice.full_data?.is_devolucao === true;
                        const isGarantiaNFe = (invoice.tipo_nota && invoice.tipo_nota.startsWith("garantia")) || invoice.full_data?.is_garantia === true;
                        const jaDevolvido = !!invoice.devolucao_id || !!invoice.full_data?.devolucao_id;

                        const showDevolucaoBtn = !isDevolucaoNFe && !isGarantiaNFe && !jaDevolvido;
                        const showGarantiaBtn = !isDevolucaoNFe && !isGarantiaNFe && !jaDevolvido;

                        const isChild = !!invoice.venda_original;
                        const isSecondGenChild = invoice.tipo_nota === "garantia_saida";

                        return (
                          <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-0.5">
                                {isSecondGenChild ? (
                                  <span 
                                    className="text-slate-400 font-mono font-black text-sm tracking-tighter shrink-0 select-none mr-1"
                                    title="Envio de Garantia (Filha da Entrada)"
                                  >
                                    ↳ ↳
                                  </span>
                                ) : isChild ? (
                                  <span 
                                    className="text-slate-400 font-mono font-black text-sm shrink-0 select-none mr-1"
                                    title={invoice.tipo_nota === "devolucao" ? "Devolução de Venda (Filha da Venda)" : "Entrada de Garantia (Filha da Venda)"}
                                  >
                                    ↳
                                  </span>
                                ) : null}
                                <span className="font-mono font-extrabold text-blue-600">
                                  #{invoice.order_number || "AVULSO"}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex flex-col">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-bold text-slate-800">{invoice.customer_name}</span>
                                  {isDevolucaoNFe && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-50 text-orange-705 border border-orange-200 uppercase tracking-wider">
                                      Devolução
                                    </span>
                                  )}
                                  {isGarantiaNFe && (
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wider ${
                                      invoice.tipo_nota === "garantia_entrada" || (invoice.order_number || "").startsWith("GAR-ENT-")
                                        ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                        : "bg-violet-50 text-violet-700 border-violet-200"
                                    }`}>
                                      {invoice.tipo_nota === "garantia_entrada" || (invoice.order_number || "").startsWith("GAR-ENT-") ? "Garantia - Entrada" : "Garantia - Saída"}
                                    </span>
                                  )}
                                  {jaDevolvido && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-50 text-rose-705 border border-rose-200 uppercase tracking-wider">
                                      Devolução Emitida
                                    </span>
                                  )}
                                  {invoice.nfe_email_enviado === true && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-705 border border-emerald-200 uppercase tracking-wider">
                                      E-mail Enviado
                                    </span>
                                  )}
                                </div>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  Ref: {invoice.id}
                                </span>
                                <span className="text-[10px] text-slate-500 font-medium">
                                  Emitido por: {invoice.usuario_emissor || invoice.full_data?.usuario_emissor || "Sistema"}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-800 leading-tight">
                                  {emitterConfig ? emitterConfig.razao_social : "Desconhecido"}
                                </span>
                                <span className="text-[10px] text-slate-400 font-mono mt-0.5">
                                  {emitterConfig ? emitterConfig.cnpj : cleanCnpj || "Sem CNPJ"}
                                </span>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center justify-center gap-1.5">
                                  {getStatusBadge(status, errMessage)}
                                  {(status === "processando" || status === "erro" || status === "pendente") && (
                                    <button
                                      onClick={() => handleSync(invoice)}
                                      disabled={syncingIds[invoice.id]}
                                      className={`p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all border border-slate-200 bg-white cursor-pointer hover:scale-105 active:scale-95 ${
                                        syncingIds[invoice.id] ? "animate-spin text-blue-500" : ""
                                      }`}
                                      title="Sincronizar status com Focus NFe"
                                    >
                                      <RefreshCw size={12} />
                                    </button>
                                  )}
                                </div>
                                {status === "erro" && errMessage && (
                                  <span className="text-[10px] text-red-500 font-bold max-w-[150px] leading-tight text-center break-words mt-0.5">
                                    {errMessage}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-4 text-center font-mono font-bold text-slate-650">
                              {numNota !== "-" ? `${numNota} / ${serie}` : "---"}
                            </td>
                            <td className="p-4 text-right font-mono font-extrabold text-slate-850">
                              R$ {parseFloat(
                               invoice.valor_total ||
                               invoice.total_value ||
                               invoice.focus_payload?.valor_total ||
                               invoice.full_data?.valor_total ||
                               invoice.full_data?.focus_payload?.valor_total ||
                               invoice.full_data?.order?.payment?.total ||
                               0
                             ).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="p-4 text-center text-slate-500 font-bold">
                              {emissionDate ? new Date(emissionDate).toLocaleDateString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "---"}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                
                                {/* Botão Sincronizar */}
                                <button
                                  onClick={() => handleSync(invoice)}
                                  disabled={syncingIds[invoice.id]}
                                  className={`p-1.5 text-slate-450 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-slate-250 bg-white cursor-pointer ${
                                    syncingIds[invoice.id] ? "text-blue-600" : ""
                                  }`}
                                  title="Sincronizar Status com Focus NFe"
                                >
                                  <RefreshCw size={13} className={syncingIds[invoice.id] ? "animate-spin" : ""} />
                                </button>

                                {/* Visualizar DANFE (PDF) */}
                                {danfeUrl ? (
                                  <a
                                    href={danfeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 text-slate-450 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-slate-250 bg-white"
                                    title="Visualizar DANFE (PDF)"
                                  >
                                    <Eye size={13} />
                                  </a>
                                ) : (
                                  <button disabled className="p-1.5 text-slate-300 border border-slate-100 rounded-lg cursor-not-allowed bg-slate-50">
                                    <Eye size={13} />
                                  </button>
                                )}

                                {/* Baixar XML */}
                                {xmlUrl ? (
                                  <a
                                    href={xmlUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 text-slate-450 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-slate-250 bg-white"
                                    title="Baixar XML"
                                    download={`NFe_${numNota}.xml`}
                                  >
                                    <Download size={13} />
                                  </a>
                                ) : (
                                  <button disabled className="p-1.5 text-slate-300 border border-slate-100 rounded-lg cursor-not-allowed bg-slate-50">
                                    <Download size={13} />
                                  </button>
                                )}

                                {/* Enviar E-mail */}
                                {status === "autorizada" ? (
                                  invoice.nfe_email_enviado === true ? (
                                    <button
                                      onClick={() => handleSendEmail(invoice)}
                                      disabled={syncingIds[invoice.id]}
                                      className="p-1.5 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700 rounded-lg transition-colors border border-emerald-200 cursor-pointer flex items-center gap-0.5"
                                      title="E-mail de notificação já enviado para o cliente. Clique para reenviar."
                                    >
                                      <Mail size={13} className={syncingIds[invoice.id] ? "animate-pulse" : ""} />
                                      <Check size={11} className="text-emerald-600 stroke-[3px]" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleSendEmail(invoice)}
                                      disabled={syncingIds[invoice.id]}
                                      className="p-1.5 text-slate-450 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-colors border border-slate-250 bg-white cursor-pointer"
                                      title="Enviar E-mail com XML/PDF para Cliente"
                                    >
                                      <Mail size={13} className={syncingIds[invoice.id] ? "animate-pulse text-amber-500" : ""} />
                                    </button>
                                  )
                                ) : (
                                  <button disabled className="p-1.5 text-slate-300 border border-slate-100 rounded-lg cursor-not-allowed bg-slate-50" title="E-mail disponível apenas para notas autorizadas">
                                    <Mail size={13} />
                                  </button>
                                )}

                                {/* Cancelar ou Apagar Rascunho */}
                                {showCancel ? (
                                  <button
                                    onClick={() => setCancelingInvoice(invoice)}
                                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-55 bg-white rounded-lg transition-colors border border-red-100 cursor-pointer"
                                    title="Cancelar Nota Fiscal"
                                  >
                                    <Ban size={13} />
                                  </button>
                                ) : (status === "erro" || status === "pendente" || status === "processando") ? (
                                  <button
                                    onClick={() => handleDeleteInvoiceRecord(invoice)}
                                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 bg-white rounded-lg transition-colors border border-red-200 cursor-pointer"
                                    title="Apagar rascunho de nota fiscal"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                ) : (
                                  <button disabled className="p-1.5 text-slate-300 border border-slate-100 rounded-lg cursor-not-allowed bg-slate-50" title="Cancelamento indisponível (>24h ou não autorizada)">
                                    <Ban size={13} />
                                  </button>
                                )}
                                
                                {/* Corrigir e Reemitir */}
                                {(status === "erro" || status === "pendente" || (status === "processando" && (new Date() - new Date(invoice.updated || invoice.created)) > 3 * 60 * 1000)) && (
                                  <button
                                    onClick={() => setResendingInvoice(invoice)}
                                    className="px-2 py-1 text-[10px] font-black text-blue-600 border border-blue-200 hover:bg-blue-50 bg-white rounded-lg transition-all cursor-pointer"
                                    title="Corrigir e Reemitir Nota Fiscal"
                                  >
                                    Corrigir e Reenviar
                                  </button>
                                )}

                                {/* Reemitir Nota Cancelada (Gera um novo registro com novo ID para poder emitir no Focus) */}
                                {status === "cancelada" && (
                                  <button
                                    onClick={() => {
                                      const cleanInvoice = {
                                        ...invoice,
                                        id: undefined, // Limpa para criar um novo registro com nova ref/id
                                        nfe_status: "pendente",
                                        nfe_numero: "",
                                        nfe_chave: "",
                                        nfe_xml: "",
                                        nfe_pdf: "",
                                        nfe_pdf_url: "",
                                        nfe_xml_url: "",
                                        nfe_error_message: "",
                                      };
                                      setResendingInvoice(cleanInvoice);
                                    }}
                                    className="px-2 py-1 text-[10px] font-black text-emerald-600 border border-emerald-200 hover:bg-emerald-50 bg-white rounded-lg transition-all cursor-pointer"
                                    title="Emitir uma nova nota fiscal a partir dos dados desta nota cancelada"
                                  >
                                    Reemitir Nova Nota
                                  </button>
                                )}

                                {/* Clonar Devolução */}
                                {showDevolucaoBtn && (
                                  status === "autorizada" ? (
                                    <button
                                      onClick={() => setDevolucaoInvoice(invoice)}
                                      className="px-2 py-1 text-[10px] font-black text-orange-600 border border-orange-200 hover:bg-orange-50 bg-white rounded-lg transition-all cursor-pointer"
                                      title="Clonar para Nota de Devolução"
                                    >
                                      Devolução
                                    </button>
                                  ) : (
                                    <button disabled className="px-2 py-1 text-[10px] font-bold text-slate-350 border border-slate-100 bg-slate-50 rounded-lg cursor-not-allowed">
                                      Devolução
                                    </button>
                                  )
                                )}

                                {/* Clonar Garantia */}
                                {showGarantiaBtn && (
                                  status === "autorizada" ? (
                                    <button
                                      onClick={() => setGarantiaInvoice(invoice)}
                                      className="px-2 py-1 text-[10px] font-black text-indigo-600 border border-indigo-200 hover:bg-indigo-50 bg-white rounded-lg transition-all cursor-pointer"
                                      title="Clonar para Garantia / Manutenção"
                                    >
                                      Garantia
                                    </button>
                                  ) : (
                                    <button disabled className="px-2 py-1 text-[10px] font-bold text-slate-350 border border-slate-100 bg-slate-50 rounded-lg cursor-not-allowed">
                                      Garantia
                                    </button>
                                  )
                                )}
                                
                                {/* Emitir Saída de Garantia */}
                                {isGarantiaNFe && (invoice.order_number || "").startsWith("GAR-ENT-") && status === "autorizada" && !invoice.garantia_saida_gerada && !invoice.full_data?.garantia_saida_gerada && (
                                  <button
                                    onClick={() => setExitGarantiaInvoice(invoice)}
                                    className="px-2 py-1 text-[10px] font-black text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-all cursor-pointer"
                                    title="Emitir Nota Fiscal de Saída Referenciada"
                                  >
                                    SAÍDA-GARANTIA
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Rodapé da Tabela (Paginação) */}
                <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between shrink-0 select-none">
                  <span className="text-xs font-bold text-slate-550">
                    Mostrando {invoices.length} de {totalItems} notas fiscais
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className="p-1.5 border border-slate-300 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer bg-white"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-xs font-black text-slate-700">
                      Página {page} de {totalPages}
                    </span>
                    <button
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className="p-1.5 border border-slate-300 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer bg-white"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* --- ABA DO DASHBOARD FISCAL --- */}
        {activeSubTab === "dashboard" && (
          <div className="p-6 space-y-6 max-w-[1400px] mx-auto animate-fade-in">
            {loadingDash ? (
              <div className="flex flex-col items-center justify-center text-slate-450 gap-3 py-32">
                <RefreshCw className="animate-spin text-blue-500" size={32} />
                <span className="text-sm font-bold">Processando métricas e relatórios fiscais...</span>
              </div>
            ) : (
              <>
                {/* 1. Grade de Cards Resumo */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Faturamento Geral */}
                  <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-xl shadow-blue-100">
                    <div className="absolute right-3 top-3 opacity-15">
                      <TrendingUp size={80} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-blue-100 flex items-center gap-1">
                      <Layers size={10} /> Faturamento em Notas
                    </span>
                    <h3 className="text-2xl font-black mt-2 font-mono leading-none">
                      R$ {dashStats.totalGeral.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </h3>
                    <p className="text-xs text-blue-100/80 mt-2 font-bold">
                      {dashStats.countGeral} nota(s) emitida(s) e autorizada(s)
                    </p>
                  </div>

                  {/* Cada Empresa Faturado */}
                  {dashStats.companies.map((company, idx) => {
                    const isSubli = company.razao_social.toLowerCase().includes("subli");
                    const grad = isSubli 
                      ? "from-emerald-600 to-teal-700 shadow-emerald-50" 
                      : "from-blue-500 to-sky-600 shadow-blue-50";
                    const subtitleColor = isSubli ? "text-emerald-100" : "text-blue-100";
                    
                    return (
                      <div key={company.cnpj || idx} className={`relative overflow-hidden bg-gradient-to-br ${grad} rounded-2xl p-6 text-white shadow-xl`}>
                        <div className="absolute right-3 top-3 opacity-15">
                          <Landmark size={80} />
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-wider ${subtitleColor} flex items-center gap-1`}>
                          <Landmark size={10} /> {company.razao_social}
                        </span>
                        <h3 className="text-2xl font-black mt-2 font-mono leading-none font-mono">
                          R$ {company.total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </h3>
                        <p className={`text-xs ${subtitleColor}/80 mt-2 font-bold`}>
                          {company.count} nota(s) autorizada(s)
                        </p>
                      </div>
                    );
                  })}

                  {/* Imposto Estimado Geral */}
                  <div className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <div className="absolute right-3 top-3 opacity-5 text-slate-850">
                      <Percent size={80} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-405 flex items-center gap-1">
                      <Landmark size={12} className="text-slate-400" /> Imposto Estimado Geral
                    </span>
                    <h3 className="text-2xl font-black mt-2 font-mono leading-none text-slate-800">
                      R$ {(dashStats.totalGeral * (taxRate / 100)).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </h3>
                    
                    {/* Alíquota interativa */}
                    <div className="flex items-center gap-1.5 mt-2 text-xs font-bold text-slate-500">
                      <span>Alíquota:</span>
                      <input
                        type="number"
                        step="0.1"
                        className="w-14 border border-slate-300 rounded-lg px-1.5 py-0.5 text-center text-xs font-bold text-slate-700 bg-slate-50 outline-none focus:border-blue-500"
                        value={taxRate}
                        onChange={(e) => setTaxRate(Math.max(0, parseFloat(e.target.value) || 0))}
                      />
                      <span>%</span>
                    </div>
                  </div>
                </div>

                {/* 2. Gráfico e Detalhamento da Empresa */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Gráfico de Evolução */}
                  <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col h-[400px]">
                    <div className="flex justify-between items-center mb-4 select-none">
                      <div>
                        <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <TrendingUp size={14} className="text-blue-500" />
                          Curva de Faturamento Diário (BRL)
                        </h4>
                        <p className="text-[10px] text-slate-400">Evolução acumulada no período selecionado</p>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Sempre Joias</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Sublismith</span>
                      </div>
                    </div>

                    <div className="flex-1 min-h-0 text-xs">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorSempre" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorSubli" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="displayDate" stroke="#94a3b8" fontSize={10} fontStyle="bold" />
                          <YAxis stroke="#94a3b8" fontSize={10} fontStyle="bold" />
                          <Tooltip 
                            formatter={(value) => [`R$ ${parseFloat(value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, undefined]}
                            labelFormatter={(label) => `Data: ${label}`}
                            contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="semprejoias" 
                            name="Sempre Joias" 
                            stroke="#3B82F6" 
                            strokeWidth={2.5}
                            fillOpacity={1} 
                            fill="url(#colorSempre)" 
                          />
                          <Area 
                            type="monotone" 
                            dataKey="sublismith" 
                            name="Sublismith" 
                            stroke="#10B981" 
                            strokeWidth={2.5}
                            fillOpacity={1} 
                            fill="url(#colorSubli)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Resumo por Empresa e Impostos */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
                    <div>
                      <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5 mb-4 select-none">
                        <Landmark size={14} className="text-slate-500" />
                        Detalhamento Contábil
                      </h4>

                      <div className="space-y-4">
                        {dashStats.companies.map((company, idx) => {
                          const isSubli = company.razao_social.toLowerCase().includes("subli");
                          const estimatedImposto = company.total * (taxRate / 100);
                          
                          return (
                            <div key={company.cnpj || idx} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h5 className="text-xs font-bold text-slate-800">{company.razao_social}</h5>
                                  <p className="text-[10px] font-mono text-slate-400 mt-0.5">CNPJ: {company.cnpj || "Sem CNPJ"}</p>
                                </div>
                                <span className={`inline-block text-[8px] font-black uppercase px-2 py-0.5 rounded-full border ${
                                  isSubli ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-blue-50 text-blue-600 border-blue-100"
                                }`}>
                                  Simples Nac.
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-2 mt-3 text-xs select-none">
                                <div className="bg-slate-50 p-2 rounded-xl text-center">
                                  <span className="text-[9px] font-bold text-slate-400 block uppercase">Faturado</span>
                                  <span className="font-extrabold text-slate-750 font-mono">
                                    R$ {company.total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="bg-slate-50 p-2 rounded-xl text-center">
                                  <span className="text-[9px] font-bold text-slate-400 block uppercase">Imp. Estimado</span>
                                  <span className="font-extrabold text-emerald-600 font-mono">
                                    R$ {estimatedImposto.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>

                              <div className="flex justify-between text-[10px] font-bold text-slate-500 mt-2.5 px-1">
                                <span>Notas Autorizadas: {company.count}</span>
                                {company.canceladas > 0 && <span className="text-slate-400">Canceladas: {company.canceladas}</span>}
                                {company.erros > 0 && <span className="text-red-500">Com Erros: {company.erros}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-100 bg-slate-50/50 -mx-6 -mb-6 p-6 rounded-b-2xl">
                      <div className="flex gap-2 items-start text-[10px] text-slate-450 leading-tight">
                        <HelpCircle size={14} className="shrink-0 text-slate-400 mt-0.5" />
                        <p>
                          Os cálculos de faturamento e imposto consideram exclusivamente notas fiscais eletrônicas de saída com status <strong>"Autorizada"</strong> no período selecionado. Devoluções e notas canceladas/com erro não compõem a receita bruta tributável.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
